import { DEVICES } from '@guuru/chat-common';
import {
  CHAT_FLOW_STEPS,
  appendLocalMessage,
  ChatAPI,
  botMessagesTypes,
  getCurrentChat,
  setBotTyping,
  setConnectionError,
  setCurrentChatSession,
  setFlowStep,
  GET_CHAT,
} from '@guuru/chat-web';
import events from '@guuru/events-web';
import { client, errorCode } from '@guuru/graphql-web';
import { i18n } from '@guuru/translation-web';
import addMessagesNotYetOnAPI from './addMessagesNotYetOnAPI';
import createOrUpdateUser from './createOrUpdateUser';
import CREATE_CHAT from './mutations/CreateChat';

const buildAutomationBehavior = (automationBehavior) => {
  if (!automationBehavior) return {};

  const { possibleIntents, proposedIntent, label, subscriptionPriority } =
    automationBehavior;

  return {
    label,
    proposedIntent,
    subscriptionPriority,
    showDecisionTree: !!possibleIntents,
  };
};
const getChannel = () => {
  const channel = ChatAPI.storeRetrieveChannel();
  if (['chat', 'form', 'community'].includes(channel)) {
    return channel;
  }
  return 'chat';
};

export default async function (partnerId) {
  try {
    // if we already have a chat just add the messages that are not yet there
    const { chatId: currChatId } = getCurrentChat();
    if (currChatId) {
      const messages = await addMessagesNotYetOnAPI(partnerId, currChatId);
      return {
        updatedChat: { messages: { edges: messages } },
        chatId: currChatId,
      };
    }

    const question = ChatAPI.storeRetrieveQuestion();
    const categoryId =
      ChatAPI.storeRetrieveCategory() ||
      ChatAPI.storeRetrieveCategoryParameter();
    const automationBehavior =
      ChatAPI.storeRetrieveAutomationBehavior(partnerId);
    const messages = ChatAPI.storeRetrievePendingMessages();
    const priority = ChatAPI.storeRetrieveChatPriority();
    const locale = ChatAPI.storeRetrieveLocale();
    const params = ChatAPI.storeRetrieveParams();
    const { token, ...tracking } = params;
    const channel = getChannel();
    const deviceType = DEVICES.getByWidth(window.screen.availWidth);

    await createOrUpdateUser(partnerId);
    const {
      data: { createChat: createdChat },
    } = await client.mutate({
      mutation: CREATE_CHAT,
      variables: {
        input: {
          partnerId,
          channel,
          category: categoryId,
          messages,
          languageCode: locale,
          question,
          automationBehavior: buildAutomationBehavior(automationBehavior),
          token,
          priority: priority || params.priority,
          deviceType,
          tracking: {
            ...tracking,
            isUser: ChatAPI.isUser(partnerId),
            isLead: ChatAPI.isLead(partnerId),
          },
          customMeta: ChatAPI.storeRetrieveCustomMeta(partnerId),
        },
      },
    });
    events.newChat(deviceType, {
      chatId: createdChat.id,
    });

    client.writeQuery({
      query: GET_CHAT,
      variables: {
        id: createdChat.id,
        isVisibleByExpert: false,
        isVisibleByUser: true,
      },
      data: {
        chat: createdChat,
      },
    });
    const { id: chatId, token: chatControlToken, ...updatedChat } = createdChat;

    events.chatCreated(chatId);
    ChatAPI.storeChatId(partnerId, chatId);
    ChatAPI.storeResetPendingMessages();
    setCurrentChatSession({ chatId });
    ChatAPI.storeChatControlToken(partnerId, chatId, chatControlToken);
    setConnectionError(false);

    const chatUrl = ChatAPI.getChatURL(partnerId, channel, {
      chatId,
      token: chatControlToken,
      qs: window.location.search,
    });
    window.history.pushState({}, null, chatUrl);

    return {
      chatId,
      updatedChat,
    };
  } catch (error) {
    const errCode = errorCode(error);
    if (['FORBIDDEN', 'UNAUTHENTICATED'].includes(errCode)) {
      const errorMessage =
        errCode === 'FORBIDDEN'
          ? i18n.t(botMessagesTypes.connectionFailed)
          : 'Your token is no longer valid';
      const message = await ChatAPI.addBotMessage(errorMessage);
      appendLocalMessage(message);
      setFlowStep(CHAT_FLOW_STEPS.hasError);
      setBotTyping(false);
      return {};
    }
    setConnectionError(true);
    throw error;
  }
}
